import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    AlignmentType,
    Footer,
    PageNumber,
    Header,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    TabStopType,
    PageOrientation,
    VerticalAlign,
    TableLayoutType,
    TableOfContents,
    PageBreak,
    convertInchesToTwip
} from "docx";
import { saveAs } from "file-saver";
// One rule for unique form codes, shared with the server (2026-09-14).
import { uniqueCodesByName } from "../../../functions/src/utils/formCodeAssignment";
// One definition of the study's date format, shared with the server (2026-10-12).
import { dateCompactMask, dateHint, dateMask, resolveStudyDateFormat } from "../../../functions/src/studyProfile/studyDateFormat";

// =============================================================================
// CRF TYPE DEFINITIONS (Matching backend crfTypes.ts)
// =============================================================================

export type CRFResponseType =
    | 'radio'
    | 'checkbox'
    | 'text'
    | 'text_with_radio'
    | 'date'
    | 'time'
    | 'datetime'
    | 'numeric'
    | 'numeric_with_unit_select'
    | 'coded'
    | 'yes_no'
    | 'date_or_continuing'
    | 'sequence';

export interface CRFOption {
    label: string;
    value: number | string;
    variable_suffix?: string;
}

export interface CRFQuestion {
    number: number | string;
    text: string;
    variable: string;
    response_type: CRFResponseType;
    options?: CRFOption[];
    format?: string;
    unit_options?: CRFOption[];
    indent?: number;
    conditional?: string;
    skip_instruction?: string;
    unknown_option?: boolean;
    not_recorded_option?: boolean;
    date_format?: string;
    time_format?: string;
}

export interface CRFSection {
    title: string | null;
    instructions?: string;
    questions: CRFQuestion[];
    /**
     * Defaults to 'questions' when absent. When 'repeating', the renderer
     * emits a column-headered table (one column per question, expectedRows
     * blank data rows) instead of a vertical question list.
     */
    kind?: 'questions' | 'repeating';
    /** Pre-printed row count for repeating sections. Default 6 when absent. */
    expectedRows?: number;
}

export interface CRFColumn {
    header: string;
    variable: string;
    type: CRFResponseType;
    code_table?: string;
    width: 'narrow' | 'medium' | 'wide';
    format?: string;
}

export interface CRFCodeEntry {
    value: number;
    label: string;
}

export interface CRFCodeTable {
    id: string;
    title: string;
    codes: CRFCodeEntry[];
}

export interface CRFClosingBlock {
    type: 'signature' | 'staff_id' | 'investigator_signature';
    label: string;
    format?: string;
    includes_date?: boolean;
}

export interface CRFHeaderField {
    label: string;
    format: string;
}

export interface CRFHeader {
    study_name: string;
    form_title: string;
    fields: CRFHeaderField[];
}

export interface CRFFooter {
    version: string;
    form_id: string;
}

export interface CRFForm {
    id: string;
    name: string;
    /** The study's date format (Study Settings). Absent prints the default, DD-MMM-YYYY. */
    date_format?: string;
    layout: 'question-list' | 'log-table';
    orientation: 'portrait' | 'landscape';
    category: 'one-time' | 'every-visit' | 'periodic' | 'as-needed';
    header: CRFHeader;
    instructions?: string;
    sections?: CRFSection[];
    columns?: CRFColumn[];
    repeating_rows?: number;
    code_tables?: CRFCodeTable[];
    closing: CRFClosingBlock[];
    footer: CRFFooter;
}

/**
 * Derive a short, human-readable form code from a CRF form name.
 * Mirrors the backend helper at functions/src/utils/formCode.ts so that
 * codes match between the AI-generated chapter heading and the form
 * lookup map used by the docx assembler.
 */
export function deriveFormCode(name: string | undefined | null): string {
    if (!name) return "FORM";
    let cleaned = name.replace(/\([^)]*\)/g, " ");
    cleaned = cleaned
        .replace(/\bForm\b/gi, " ")
        .replace(/[-–—]\s*Page\s+\d+/gi, " ");
    cleaned = cleaned.replace(/[^A-Za-z0-9\s-]/g, " ");
    const words = cleaned
        .split(/[\s-]+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 0);
    if (words.length === 0) return "FORM";
    if (words.length === 1) return words[0].substring(0, 4).toUpperCase();
    const stopwords = new Set([
        "of", "and", "the", "to", "in", "on", "for", "with", "a", "an",
        "from", "at", "by", "or",
    ]);
    const significant = words.filter((w) => !stopwords.has(w.toLowerCase()));
    return (significant.length > 0 ? significant : words)
        .map((w) => w.charAt(0).toUpperCase())
        .join("")
        .substring(0, 6);
}

/**
 * Study metadata used to populate the title page and signature block of
 * the CRF Completion Guidelines packet.
 *
 * Fields are all optional — the title page degrades gracefully when data
 * is missing, showing "[Field not specified]" placeholders rather than
 * crashing. The download handler in DataManagementHub.tsx is responsible
 * for assembling this object from the project doc and studySetup.
 */
export interface CRFGuideMetadata {
    studyTitle?: string;
    studyShortName?: string;
    sponsor?: string;
    coordinatingCenter?: string;
    protocolNumber?: string;
    edcSystem?: string;
    version?: string;
    documentDate?: string;     // ISO date or display string
    confidentialityStatement?: string;
    /** The study's date format, so each embedded CRF prints it. Absent prints the default. */
    dateFormat?: string;
}

// =============================================================================
// CRF RENDERER CONSTANTS
// =============================================================================

const FONT_NAME = 'Arial';
const FONT_SIZE = 20; // 10pt in half-points
const FONT_SIZE_SMALL = 18; // 9pt
const WINGDINGS_CHECKBOX = '\uF071'; // Ballot box in Wingdings
const TAB_STOP_RESPONSE = convertInchesToTwip(2.5);

/**
 * Converts formatted text (handling basic markdown-like bolding) to TextRun[]
 */
const formatText = (text: string): TextRun[] => {
    // Simple bold detection for **text**
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map(part => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return new TextRun({
                text: part.slice(2, -2),
                bold: true,
                size: 24, // 12pt
                font: "Calibri"
            });
        }
        return new TextRun({
            text: part,
            size: 24, // 12pt
            font: "Calibri"
        });
    });
};

/**
 * Detect a markdown table separator row like `| --- | --- |`
 */
const isMarkdownTableSeparator = (line: string): boolean => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return false;
    // Strip outer pipes and check each cell is dashes (with optional alignment colons)
    const inner = trimmed.slice(1, -1);
    return inner.split('|').every(cell => /^\s*:?-{1,}:?\s*$/.test(cell));
};

/**
 * Split a markdown table row into cell strings (handles escaped pipes \|)
 */
const splitMarkdownRow = (line: string): string[] => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return [];
    return trimmed
        .slice(1, -1)
        .split(/(?<!\\)\|/)
        .map(cell => cell.trim().replace(/\\\|/g, '|'));
};

/**
 * Build a docx Table from a parsed markdown table.
 * The first row is treated as the header.
 */
const buildMarkdownTable = (rows: string[][]): Table => {
    if (rows.length === 0) {
        return new Table({ rows: [], width: { size: 100, type: WidthType.PERCENTAGE } });
    }
    const columnCount = Math.max(...rows.map(r => r.length));
    const headerRow = rows[0];
    const bodyRows = rows.slice(1);

    const padRow = (row: string[]): string[] => {
        const padded = [...row];
        while (padded.length < columnCount) padded.push('');
        return padded;
    };

    const cellBorder = { style: BorderStyle.SINGLE, size: 4, color: '999999' };
    const borders = {
        top: cellBorder,
        bottom: cellBorder,
        left: cellBorder,
        right: cellBorder,
    };

    const docxRows: TableRow[] = [];

    // Header row
    docxRows.push(
        new TableRow({
            tableHeader: true,
            children: padRow(headerRow).map(cellText =>
                new TableCell({
                    width: { size: Math.floor(100 / columnCount), type: WidthType.PERCENTAGE },
                    shading: { fill: 'E8E8E8' },
                    margins: { top: 80, bottom: 80, left: 100, right: 100 },
                    children: [new Paragraph({
                        children: [new TextRun({ text: cellText, bold: true, size: 20, font: 'Calibri' })],
                        spacing: { after: 0 }
                    })],
                })
            ),
        })
    );

    // Body rows
    for (const row of bodyRows) {
        docxRows.push(
            new TableRow({
                children: padRow(row).map(cellText =>
                    new TableCell({
                        width: { size: Math.floor(100 / columnCount), type: WidthType.PERCENTAGE },
                        margins: { top: 60, bottom: 60, left: 100, right: 100 },
                        children: [new Paragraph({
                            children: formatText(cellText || ''),
                            spacing: { after: 0 }
                        })],
                    })
                ),
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        layout: TableLayoutType.AUTOFIT,
        borders: {
            top: cellBorder,
            bottom: cellBorder,
            left: cellBorder,
            right: cellBorder,
            insideHorizontal: cellBorder,
            insideVertical: cellBorder,
        },
        rows: docxRows,
    });
};

/**
 * Parse markdown text (like CRF content) into properly formatted Word
 * paragraphs and tables.
 */
const parseMarkdownToBlocks = (markdown: string): (Paragraph | Table)[] => {
    const blocks: (Paragraph | Table)[] = [];
    const lines = markdown.split('\n');

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Skip empty lines but add spacing
        if (!trimmed) {
            blocks.push(new Paragraph({
                text: "",
                spacing: { after: 60 }
            }));
            continue;
        }

        // Heading 1 (# Header)
        if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
            blocks.push(new Paragraph({
                text: trimmed.substring(2),
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 400, after: 200 },
                alignment: AlignmentType.CENTER
            }));
            continue;
        }

        // Heading 2 (## Header)
        if (trimmed.startsWith('## ')) {
            blocks.push(new Paragraph({
                text: trimmed.substring(3),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 150 }
            }));
            continue;
        }

        // Heading 3 (### Header)
        if (trimmed.startsWith('### ')) {
            blocks.push(new Paragraph({
                text: trimmed.substring(4),
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 120 }
            }));
            continue;
        }

        // Horizontal rule (---)
        if (trimmed === '---' || /^-{3,}$/.test(trimmed)) {
            blocks.push(new Paragraph({
                text: "―――――――――――――――――――――――――――――――――――――――――",
                spacing: { before: 200, after: 200 }
            }));
            continue;
        }

        // Markdown table block: `|...|` followed by `|---|---|` separator
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            // Look ahead — is the next non-empty line a separator?
            let nextIdx = i + 1;
            while (nextIdx < lines.length && lines[nextIdx].trim() === '') nextIdx++;
            const nextLine = nextIdx < lines.length ? lines[nextIdx] : '';

            if (isMarkdownTableSeparator(nextLine)) {
                // Collect the full table
                const tableRows: string[][] = [splitMarkdownRow(trimmed)];
                let j = nextIdx + 1;
                while (j < lines.length) {
                    const t = lines[j].trim();
                    if (!t.startsWith('|') || !t.endsWith('|')) break;
                    if (isMarkdownTableSeparator(lines[j])) { j++; continue; }
                    tableRows.push(splitMarkdownRow(t));
                    j++;
                }
                blocks.push(buildMarkdownTable(tableRows));
                // Add a small spacer paragraph after the table
                blocks.push(new Paragraph({ text: '', spacing: { after: 120 } }));
                i = j - 1;
                continue;
            }

            // Lone pipe-style line that isn't a real table — render as plain paragraph
            blocks.push(new Paragraph({
                children: formatText(trimmed),
                spacing: { after: 60 },
            }));
            continue;
        }

        // Lists (starting with - or *)
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            const textRuns = formatText(trimmed.substring(2));
            blocks.push(new Paragraph({
                children: textRuns,
                bullet: { level: 0 },
                spacing: { after: 120 }
            }));
            continue;
        }

        // Numbered lists
        if (/^\d+\./.test(trimmed)) {
            const textRuns = formatText(trimmed);
            blocks.push(new Paragraph({
                children: textRuns,
                numbering: { reference: "default-numbering", level: 0 },
                spacing: { after: 120 }
            }));
            continue;
        }

        // Regular paragraphs
        const textRuns = formatText(trimmed);
        blocks.push(new Paragraph({
            children: textRuns,
            spacing: { after: 120 }
        }));
    }

    return blocks;
};

/**
 * Backwards-compat wrapper that flattens blocks to paragraphs only.
 * Used by older callers that haven't migrated to the block-aware API.
 */
const parseMarkdownToParagraphs = (markdown: string): Paragraph[] => {
    return parseMarkdownToBlocks(markdown).filter(
        (b): b is Paragraph => b instanceof Paragraph
    );
};

/**
 * Recursive function to traverse JSON and build Docx Paragraphs
 */
const processNode = (key: string | null, value: any, level: number = 0): Paragraph[] => {
    const paragraphs: Paragraph[] = [];
    const indent = level * 720; // 0.5 inch per level

    // Case 1: Value is a simple string/number/boolean
    if (typeof value !== 'object' || value === null) {
        // If there's a key, make it bold followed by the value
        if (key) {
            // Skip "content" keys if they are just huge blocks, but keep them for fields
            const label = key.replace(/([A-Z])/g, ' $1').trim().replace(/_/g, ' ');

            paragraphs.push(new Paragraph({
                children: [
                    new TextRun({ text: `${label}: `, bold: true, size: 24, font: "Calibri" }),
                    ...formatText(String(value))
                ],
                indent: { left: indent },
                spacing: { after: 120 }
            }));
        } else {
            // Just a value (e.g. array item)
            paragraphs.push(new Paragraph({
                children: formatText(String(value)),
                indent: { left: indent },
                bullet: { level: 0 }, // Treat as list item if no key
                spacing: { after: 120 }
            }));
        }
        return paragraphs;
    }

    // Case 2: Value is an Array
    if (Array.isArray(value)) {
        if (key) {
            const label = key.replace(/([A-Z])/g, ' $1').trim().replace(/_/g, ' ');
            paragraphs.push(new Paragraph({
                text: label,
                heading: HeadingLevel.HEADING_3,
                indent: { left: indent },
                spacing: { before: 240, after: 120 }
            }));
        }

        value.forEach(item => {
            paragraphs.push(...processNode(null, item, level + 1));
        });
        return paragraphs;
    }

    // Case 3: Value is an Object
    if (typeof value === 'object') {
        // If it's a root or named section object
        if (key) {
            const label = key.replace(/([A-Z])/g, ' $1').trim().replace(/_/g, ' ');
            // Determine heading level based on depth
            let headingLevel: any = HeadingLevel.HEADING_4;
            if (level === 0) headingLevel = HeadingLevel.HEADING_1;
            if (level === 1) headingLevel = HeadingLevel.HEADING_2;
            if (level === 2) headingLevel = HeadingLevel.HEADING_3;

            paragraphs.push(new Paragraph({
                text: label,
                heading: headingLevel,
                indent: { left: indent },
                spacing: { before: 240, after: 120 }
            }));
        }

        Object.entries(value).forEach(([childKey, childValue]) => {
            paragraphs.push(...processNode(childKey, childValue, level + 1));
        });
        return paragraphs;
    }

    return paragraphs;
};


export const generateDocx = async (data: any, title: string) => {
    let contentBlocks: (Paragraph | Table)[] = [];

    // Parse if string
    let parsableData = data;
    if (typeof data === 'string') {
        try {
            parsableData = JSON.parse(data);
        } catch {
            // If strictly string (markdown), parse it properly (with table support)
            contentBlocks = parseMarkdownToBlocks(data);
        }
    }

    if (typeof parsableData === 'object') {
        contentBlocks = processNode(null, parsableData);
    }

    const doc = new Document({
        styles: {
            paragraphStyles: [
                {
                    id: "Normal",
                    name: "Normal",
                    run: {
                        font: "Arial",
                        size: 24, // 12pt
                        color: "000000"
                    },
                    paragraph: {
                        spacing: {
                            line: 276,
                            before: 0,
                            after: 120,
                        },
                    },
                },
                {
                    id: "Heading1",
                    name: "Heading 1",
                    run: {
                        font: "Arial",
                        size: 28, // 14pt
                        bold: true,
                        color: "000000"
                    },
                    paragraph: {
                        spacing: { before: 240, after: 120 },
                    },
                },
            ],
        },
        sections: [{
            properties: {
                page: {
                    margin: {
                        top: 1440, // 1 inch
                        right: 1440,
                        bottom: 1440,
                        left: 1440,
                    },
                },
            },
            footers: {
                default: new Footer({
                    children: [
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new TextRun({
                                    children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
                                    font: "Arial",
                                    size: 20, // 10pt for footer
                                }),
                            ],
                        }),
                    ],
                }),
            },
            children: [
                new Paragraph({
                    text: title,
                    heading: HeadingLevel.TITLE,
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 400 },
                    run: { font: "Arial", bold: true, color: "000000" }
                }),
                new Paragraph({
                    text: `Generated: ${new Date().toLocaleDateString()}`,
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 400 },
                    run: { font: "Arial", color: "000000" }
                }),
                ...contentBlocks
            ],
        }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`);
};

// =============================================================================
// CRF DOCX GENERATOR - Professional Paper CRF Rendering
// =============================================================================

/**
 * Check if content is a structured CRF form
 */
export function isStructuredCRF(content: string): boolean {
    try {
        const parsed = JSON.parse(content);
        return parsed && parsed.id && parsed.layout && (parsed.sections || parsed.columns);
    } catch {
        return false;
    }
}

/**
 * Parse CRF form from JSON string
 */
export function parseCRFForm(content: string): CRFForm | null {
    try {
        return JSON.parse(content) as CRFForm;
    } catch {
        return null;
    }
}

/**
 * Create a Wingdings checkbox TextRun
 */
const createCheckbox = (): TextRun => {
    return new TextRun({
        text: WINGDINGS_CHECKBOX,
        font: 'Wingdings',
        size: FONT_SIZE
    });
};

/**
 * Render response elements based on question type
 */
const renderCRFResponse = (question: CRFQuestion, dateFormat: string): TextRun[] => {
    const elements: TextRun[] = [];

    switch (question.response_type) {
        case 'radio':
            if (question.options) {
                if (question.options.length <= 3 && question.options.every(o => o.label.length < 15)) {
                    for (const option of question.options) {
                        elements.push(createCheckbox());
                        elements.push(new TextRun({
                            text: ` ${option.label}  `,
                            font: FONT_NAME,
                            size: FONT_SIZE
                        }));
                    }
                } else {
                    elements.push(createCheckbox());
                    elements.push(new TextRun({
                        text: ` ${question.options[0].label}`,
                        font: FONT_NAME,
                        size: FONT_SIZE
                    }));
                }
            }
            break;

        case 'checkbox':
            if (question.options) {
                for (const option of question.options) {
                    elements.push(createCheckbox());
                    elements.push(new TextRun({
                        text: ` ${option.label}  `,
                        font: FONT_NAME,
                        size: FONT_SIZE
                    }));
                }
            }
            break;

        case 'text':
            elements.push(new TextRun({
                text: '________________________________',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            break;

        case 'text_with_radio':
            elements.push(new TextRun({
                text: '________________  ',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            if (question.options) {
                for (const option of question.options) {
                    elements.push(createCheckbox());
                    elements.push(new TextRun({
                        text: ` ${option.label}  `,
                        font: FONT_NAME,
                        size: FONT_SIZE
                    }));
                }
            }
            break;

        case 'date':
            elements.push(new TextRun({
                text: `${dateMask(dateFormat)}  `,
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            elements.push(new TextRun({
                // The study's date format (Study Settings), default DD-MMM-YYYY.
                // A format stamped on the question by an earlier build is not used.
                text: dateHint(dateFormat),
                font: FONT_NAME,
                size: FONT_SIZE_SMALL,
                italics: true
            }));
            break;

        case 'time':
            elements.push(new TextRun({
                text: '__ __ : __ __  ',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            elements.push(new TextRun({
                text: '(hh:mm, 24h)',
                font: FONT_NAME,
                size: FONT_SIZE_SMALL,
                italics: true
            }));
            break;

        case 'datetime':
            elements.push(new TextRun({
                text: `${dateMask(dateFormat)}  `,
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            elements.push(new TextRun({
                text: '__ __ : __ __  ',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            if (question.not_recorded_option) {
                elements.push(createCheckbox());
                elements.push(new TextRun({
                    text: ' Not recorded',
                    font: FONT_NAME,
                    size: FONT_SIZE
                }));
            }
            break;

        case 'numeric':
            elements.push(new TextRun({
                text: question.format || '___ ___ ___',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            if (question.unknown_option) {
                elements.push(new TextRun({ text: '   ', font: FONT_NAME, size: FONT_SIZE }));
                elements.push(createCheckbox());
                elements.push(new TextRun({
                    text: ' Unknown',
                    font: FONT_NAME,
                    size: FONT_SIZE
                }));
            }
            break;

        case 'numeric_with_unit_select':
            elements.push(new TextRun({
                text: question.format || '___ ___ ___',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            elements.push(new TextRun({ text: '  ', font: FONT_NAME, size: FONT_SIZE }));
            if (question.unit_options) {
                for (const unit of question.unit_options) {
                    elements.push(createCheckbox());
                    elements.push(new TextRun({
                        text: ` ${unit.label}  `,
                        font: FONT_NAME,
                        size: FONT_SIZE
                    }));
                }
            }
            if (question.unknown_option) {
                elements.push(createCheckbox());
                elements.push(new TextRun({
                    text: ' Unknown',
                    font: FONT_NAME,
                    size: FONT_SIZE
                }));
            }
            break;

        case 'yes_no':
            elements.push(new TextRun({
                text: 'Yes   No',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            break;

        case 'date_or_continuing':
            elements.push(new TextRun({
                text: `${dateMask(dateFormat)}  |  `,
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            elements.push(createCheckbox());
            elements.push(new TextRun({
                text: ' Continuing',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
            break;

        default:
            elements.push(new TextRun({
                text: '________________',
                font: FONT_NAME,
                size: FONT_SIZE
            }));
    }

    return elements;
};

/**
 * Render a single CRF question
 */
const renderCRFQuestion = (question: CRFQuestion, dateFormat: string): Paragraph[] => {
    const paragraphs: Paragraph[] = [];
    const indent = (question.indent || 0) * convertInchesToTwip(0.25);
    const questionText = `${String(question.number)}.  ${question.text}`;
    const responseElements = renderCRFResponse(question, dateFormat);

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: questionText,
                    font: FONT_NAME,
                    size: FONT_SIZE
                }),
                new TextRun({ text: '\t', font: FONT_NAME, size: FONT_SIZE }),
                ...responseElements
            ],
            tabStops: [{ type: TabStopType.LEFT, position: TAB_STOP_RESPONSE }],
            spacing: { before: 100, after: 50 },
            indent: { left: indent }
        })
    );

    if (question.skip_instruction) {
        paragraphs.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: question.skip_instruction,
                        font: FONT_NAME,
                        size: FONT_SIZE,
                        bold: true,
                        italics: true
                    })
                ],
                spacing: { before: 50, after: 50 },
                indent: { left: TAB_STOP_RESPONSE }
            })
        );
    }

    return paragraphs;
};

/**
 * Build the inner body content (header, fields, sections, closing) for a single
 * CRF form, suitable for embedding inside a larger composite document like the
 * CRF Completion Guidelines. Excludes the page setup, headers, and footers
 * that generateCrfDocx applies.
 */
export const buildCrfFormBlocks = (
    form: CRFForm,
    studyShortName?: string,
): (Paragraph | Table)[] => {
    // Defensively read fields — partial CRF artifacts in Firestore may be missing
    // header, id, or name. Use safe fallbacks rather than throwing.
    const header = form.header || ({} as CRFForm['header']);
    const studyName = studyShortName || header.study_name || 'STUDY';
    const formName = form.name || 'Unnamed Form';
    const formId = form.id || 'FORM';
    const blocks: (Paragraph | Table)[] = [];

    // Inline form title (since the embedded form has no page header)
    blocks.push(
        new Paragraph({
            children: [
                new TextRun({ text: studyName, font: FONT_NAME, size: FONT_SIZE, bold: true }),
                new TextRun({ text: ' — ', font: FONT_NAME, size: FONT_SIZE }),
                new TextRun({ text: formName, font: FONT_NAME, size: FONT_SIZE, bold: true }),
                new TextRun({ text: `   [${formId}]`, font: FONT_NAME, size: FONT_SIZE_SMALL })
            ],
            spacing: { before: 200, after: 100 }
        })
    );

    // Subject ID / visit / site fields displayed inline
    if (header.fields && header.fields.length > 0) {
        const headerLineRuns: TextRun[] = [];
        header.fields.forEach((f, idx) => {
            if (idx > 0) headerLineRuns.push(new TextRun({ text: '    ', font: FONT_NAME, size: FONT_SIZE_SMALL }));
            headerLineRuns.push(new TextRun({ text: `${f.label}: `, font: FONT_NAME, size: FONT_SIZE_SMALL, bold: true }));
            headerLineRuns.push(new TextRun({ text: f.format || '________', font: FONT_NAME, size: FONT_SIZE_SMALL }));
        });
        blocks.push(new Paragraph({ children: headerLineRuns, spacing: { after: 100 } }));
    }

    // Form-level instructions
    if (form.instructions) {
        blocks.push(
            new Paragraph({
                children: [new TextRun({ text: form.instructions, font: FONT_NAME, size: FONT_SIZE, italics: true })],
                spacing: { before: 50, after: 150 }
            })
        );
    }

    // Render based on layout type
    if (form.layout === 'question-list' && form.sections) {
        for (const section of form.sections) {
            if (section.title) {
                blocks.push(
                    new Paragraph({
                        children: [new TextRun({ text: section.title, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                        spacing: { before: 200, after: 60 }
                    })
                );
            }
            if (section.instructions) {
                blocks.push(
                    new Paragraph({
                        children: [new TextRun({ text: section.instructions, font: FONT_NAME, size: FONT_SIZE_SMALL, italics: true })],
                        spacing: { before: 30, after: 60 },
                        indent: { left: convertInchesToTwip(0.18) }
                    })
                );
            }
            // Repeating section → column-headered Word table with N blank
            // data rows. Mirrors the server-side acrf/docx renderer paths
            // for the same section.kind so an export from this client
            // path produces the same shape Word doc.
            if (section.kind === 'repeating') {
                const cols = section.questions || [];
                if (cols.length > 0) {
                    const numRows =
                        typeof section.expectedRows === 'number' && section.expectedRows > 0
                            ? section.expectedRows
                            : 6;
                    const rows: TableRow[] = [];
                    rows.push(new TableRow({
                        tableHeader: true,
                        children: cols.map(q =>
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        children: [new TextRun({
                                            text: q.text || q.variable || '',
                                            font: FONT_NAME, size: FONT_SIZE_SMALL, bold: true,
                                        })],
                                        alignment: AlignmentType.CENTER,
                                    }),
                                ],
                                verticalAlign: VerticalAlign.CENTER,
                            })
                        ),
                    }));
                    for (let i = 0; i < numRows; i++) {
                        rows.push(new TableRow({
                            children: cols.map(q =>
                                new TableCell({
                                    children: [
                                        new Paragraph({
                                            children: [new TextRun({
                                                text:
                                                    q.response_type === 'date'
                                                        ? dateCompactMask(resolveStudyDateFormat(form.date_format))
                                                        : q.response_type === 'time'
                                                          ? '__:__'
                                                          : q.response_type === 'yes_no'
                                                            ? 'Y   N'
                                                            : '',
                                                font: FONT_NAME, size: FONT_SIZE_SMALL,
                                            })],
                                            alignment: AlignmentType.CENTER,
                                        }),
                                    ],
                                    verticalAlign: VerticalAlign.CENTER,
                                })
                            ),
                        }));
                    }
                    blocks.push(new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        layout: TableLayoutType.FIXED,
                        rows,
                    }));
                }
                continue;
            }
            for (const question of section.questions) {
                blocks.push(...renderCRFQuestion(question, resolveStudyDateFormat(form.date_format)));
            }
        }
    } else if (form.layout === 'log-table' && form.columns) {
        const rows: TableRow[] = [];
        rows.push(new TableRow({
            tableHeader: true,
            children: form.columns.map(col =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: col.header, font: FONT_NAME, size: FONT_SIZE_SMALL, bold: true })],
                            alignment: AlignmentType.CENTER
                        })
                    ],
                    verticalAlign: VerticalAlign.CENTER
                })
            )
        }));
        const numRows = form.repeating_rows || 6;
        for (let i = 0; i < numRows; i++) {
            rows.push(new TableRow({
                children: form.columns.map(col =>
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [new TextRun({
                                    text: col.type === 'date' ? dateCompactMask(resolveStudyDateFormat(form.date_format)) : col.type === 'yes_no' ? 'Y   N' : '',
                                    font: FONT_NAME,
                                    size: FONT_SIZE_SMALL
                                })],
                                alignment: AlignmentType.CENTER
                            })
                        ],
                        verticalAlign: VerticalAlign.CENTER
                    })
                )
            }));
        }
        blocks.push(new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            layout: TableLayoutType.FIXED,
            rows
        }));
    }

    return blocks;
};

/**
 * Build the title page blocks for the CRF Completion Guidelines packet.
 * Mirrors the REFERENCE-CCG reference layout: study title, short name, version,
 * date, sponsor/coordinating-center block, and a CONFIDENTIAL property
 * statement. All fields degrade gracefully when missing.
 */
export const buildCRFGuideTitlePage = (
    metadata: CRFGuideMetadata
): (Paragraph | Table)[] => {
    const blocks: (Paragraph | Table)[] = [];

    const studyTitle = metadata.studyTitle || '[Study title not specified]';
    const shortName = metadata.studyShortName || '';
    const sponsor = metadata.sponsor || '[Sponsor not specified]';
    const coordinatingCenter = metadata.coordinatingCenter || '';
    const protocolNumber = metadata.protocolNumber || '';
    const edcSystem = metadata.edcSystem || '';
    const version = metadata.version || 'V1.0';
    const documentDate = metadata.documentDate || new Date().toLocaleDateString();
    const confidentialityStatement = metadata.confidentialityStatement ||
        'THESE DOCUMENTS CONTAIN PRIVILEGED, PROPRIETARY, OR OTHERWISE PRIVATE STUDY INFORMATION AND SHALL NOT BE REPRODUCED, DISTRIBUTED, DISCLOSED OR USED WITHOUT THE EXPRESS WRITTEN CONSENT OF THE STUDY SPONSOR.';

    // Top spacing
    blocks.push(new Paragraph({ text: '', spacing: { before: 1440 } }));

    // Full study title (large, bold, centered)
    blocks.push(
        new Paragraph({
            children: [
                new TextRun({ text: studyTitle, font: 'Calibri', size: 36, bold: true })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 240 },
        })
    );

    // Short name / acronym
    if (shortName) {
        blocks.push(
            new Paragraph({
                children: [
                    new TextRun({ text: shortName, font: 'Calibri', size: 28, bold: true })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 480 },
            })
        );
    }

    // Document title
    blocks.push(
        new Paragraph({
            children: [
                new TextRun({ text: 'Case Report Form Completion Guidelines', font: 'Calibri', size: 32, bold: true })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 360 },
        })
    );

    // Version + date
    blocks.push(
        new Paragraph({
            children: [
                new TextRun({ text: `${version}    ${documentDate}`, font: 'Calibri', size: 24 })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 720 },
        })
    );

    // Sponsor / coordinating center block
    blocks.push(
        new Paragraph({
            children: [new TextRun({ text: sponsor, font: 'Calibri', size: 22, bold: true })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 60 },
        })
    );
    if (coordinatingCenter) {
        blocks.push(
            new Paragraph({
                children: [new TextRun({ text: coordinatingCenter, font: 'Calibri', size: 22 })],
                alignment: AlignmentType.CENTER,
                spacing: { after: 60 },
            })
        );
    }
    if (protocolNumber) {
        blocks.push(
            new Paragraph({
                children: [new TextRun({ text: `Protocol Number: ${protocolNumber}`, font: 'Calibri', size: 20 })],
                alignment: AlignmentType.CENTER,
                spacing: { after: 60 },
            })
        );
    }
    if (edcSystem) {
        blocks.push(
            new Paragraph({
                children: [new TextRun({ text: `EDC System: ${edcSystem}`, font: 'Calibri', size: 20 })],
                alignment: AlignmentType.CENTER,
                spacing: { after: 60 },
            })
        );
    }

    // Spacer
    blocks.push(new Paragraph({ text: '', spacing: { before: 1440 } }));

    // Confidentiality statement (centered, bold, all-caps).
    // Wrapped in a single-cell bordered table because Paragraph-level borders
    // can produce OOXML that some Word versions reject.
    blocks.push(
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 12, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 12, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 12, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 12, color: '000000' },
                insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
                insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            margins: { top: 200, bottom: 200, left: 200, right: 200 },
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: confidentialityStatement.toUpperCase(),
                                            font: 'Calibri',
                                            size: 18,
                                            bold: true,
                                        })
                                    ],
                                    alignment: AlignmentType.CENTER,
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        })
    );

    // Page break to end the title page
    blocks.push(
        new Paragraph({
            children: [new PageBreak()],
        })
    );

    return blocks;
};

/**
 * Build the approval/signature block. Mirrors SEER's layout:
 *
 *   Author:           [Signature line]   [Print Name]
 *
 *   Approvers
 *   Clinical Data Management   [Signature]  [Print Name]  [Date]
 *   Clinical Project Management [Signature]  [Print Name]  [Date]
 *   Sponsor                     [Signature]  [Print Name]  [Date]
 *
 * Each row is a 4-column borderless table for clean alignment.
 */
export const buildCRFGuideSignaturePage = (
    metadata: CRFGuideMetadata
): (Paragraph | Table)[] => {
    const blocks: (Paragraph | Table)[] = [];

    blocks.push(
        new Paragraph({
            children: [new TextRun({ text: 'Document Approval', font: 'Calibri', size: 32, bold: true })],
            spacing: { before: 240, after: 240 },
        })
    );

    blocks.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'The undersigned hereby jointly declare that they have examined the data management plan and agree to the content as it relates to their respective responsibilities described within. Furthermore, they confirm that the CRF Completion Guidelines outlined below contain all information relevant to Case Report Form data entries collected for this study.',
                    font: 'Calibri',
                    size: 22,
                })
            ],
            spacing: { after: 480 },
        })
    );

    const noBorders = {
        top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    };

    const signatureLineRun = () => new TextRun({
        text: '________________________',
        font: 'Calibri',
        size: 22,
    });

    const labelRun = (text: string) => new TextRun({
        text,
        font: 'Calibri',
        size: 20,
        color: '666666',
    });

    const buildSignatureRow = (role: string): TableRow => new TableRow({
        children: [
            // Role label cell
            new TableCell({
                width: { size: 25, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        children: [new TextRun({ text: role, font: 'Calibri', size: 22, bold: true })],
                        spacing: { before: 240, after: 60 },
                    })
                ],
            }),
            // Signature cell
            new TableCell({
                width: { size: 35, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        children: [signatureLineRun()],
                        spacing: { before: 240, after: 0 },
                    }),
                    new Paragraph({
                        children: [labelRun('Signature')],
                        spacing: { after: 120 },
                    }),
                ],
            }),
            // Print name cell
            new TableCell({
                width: { size: 25, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        children: [signatureLineRun()],
                        spacing: { before: 240, after: 0 },
                    }),
                    new Paragraph({
                        children: [labelRun('Print Name')],
                        spacing: { after: 120 },
                    }),
                ],
            }),
            // Date cell
            new TableCell({
                width: { size: 15, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        children: [new TextRun({ text: '___ / ___ / _____', font: 'Calibri', size: 22 })],
                        spacing: { before: 240, after: 0 },
                    }),
                    new Paragraph({
                        children: [labelRun('Date')],
                        spacing: { after: 120 },
                    }),
                ],
            }),
        ],
    });

    // Author row (no date column needed)
    blocks.push(
        new Paragraph({
            children: [new TextRun({ text: 'Author', font: 'Calibri', size: 24, bold: true })],
            spacing: { before: 240, after: 120 },
        })
    );
    blocks.push(
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: noBorders,
            rows: [buildSignatureRow('Author')],
        })
    );

    // Approver section
    blocks.push(
        new Paragraph({
            children: [new TextRun({ text: 'Approvers', font: 'Calibri', size: 24, bold: true })],
            spacing: { before: 480, after: 120 },
        })
    );
    blocks.push(
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: noBorders,
            rows: [
                buildSignatureRow('Clinical Data Management'),
                buildSignatureRow('Clinical Project Management'),
                buildSignatureRow(metadata.sponsor || 'Sponsor'),
            ],
        })
    );

    // Page break to end the signature page
    blocks.push(
        new Paragraph({
            children: [new PageBreak()],
        })
    );

    return blocks;
};

/**
 * Build the Table of Contents block. The docx library's TableOfContents
 * emits a Word TOC field that resolves to actual page numbers when the
 * file is opened in Word (or when the user selects Update Field).
 *
 * The TOC pulls Heading 1 / Heading 2 / Heading 3 entries.
 */
export const buildCRFGuideTOC = (): (Paragraph | TableOfContents)[] => {
    return [
        new Paragraph({
            children: [new TextRun({ text: 'Table of Contents', font: 'Calibri', size: 32, bold: true })],
            spacing: { before: 240, after: 240 },
        }),
        new Paragraph({
            children: [
                new TextRun({
                    text: 'If page numbers below show as "Error! No table of contents entries found." or appear as placeholders, right-click the table and choose Update Field to populate it.',
                    font: 'Calibri',
                    size: 18,
                    italics: true,
                    color: '666666',
                })
            ],
            spacing: { after: 240 },
        }),
        new TableOfContents('Table of Contents', {
            hyperlink: true,
            headingStyleRange: '1-3',
        }),
        new Paragraph({
            children: [new PageBreak()],
        }),
    ];
};

/**
 * Generate professional paper CRF document
 */
export const generateCrfDocx = async (
    form: CRFForm,
    studyShortName?: string,
    version?: string
) => {
    const studyName = studyShortName || form.header.study_name || 'STUDY';
    const versionString = version || form.footer.version || 'V1.0';
    const isLandscape = form.orientation === 'landscape';

    // Build header fields
    const headerFields = form.header.fields.map(f =>
        new Paragraph({
            children: [
                new TextRun({ text: `${f.label}: `, font: FONT_NAME, size: FONT_SIZE }),
                new TextRun({ text: f.format || '________________', font: FONT_NAME, size: FONT_SIZE })
            ],
            spacing: { after: 50 }
        })
    );

    // Create 3-column header table
    const headerTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            new TableRow({
                children: [
                    // Logo placeholder
                    new TableCell({
                        width: { size: 20, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [new Paragraph({ text: '', alignment: AlignmentType.LEFT })]
                    }),
                    // Study name and form title
                    new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [
                            new Paragraph({
                                children: [new TextRun({ text: studyName, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                                alignment: AlignmentType.CENTER
                            }),
                            new Paragraph({
                                children: [new TextRun({ text: form.name, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                                alignment: AlignmentType.CENTER
                            })
                        ]
                    }),
                    // Participant fields
                    new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        verticalAlign: VerticalAlign.TOP,
                        children: headerFields
                    })
                ]
            })
        ]
    });

    // Build body content
    const bodyContent: (Paragraph | Table)[] = [];

    // Instructions
    if (form.instructions) {
        bodyContent.push(
            new Paragraph({
                children: [new TextRun({ text: form.instructions, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                spacing: { before: 100, after: 200 }
            })
        );
    }

    // Render based on layout type
    if (form.layout === 'question-list' && form.sections) {
        for (const section of form.sections) {
            if (section.title) {
                bodyContent.push(
                    new Paragraph({
                        children: [new TextRun({ text: section.title, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                        spacing: { before: 300, after: 100 }
                    })
                );
            }

            if (section.instructions) {
                bodyContent.push(
                    new Paragraph({
                        children: [new TextRun({ text: section.instructions, font: FONT_NAME, size: FONT_SIZE, bold: true })],
                        spacing: { before: 50, after: 100 },
                        indent: { left: convertInchesToTwip(0.18) }
                    })
                );
            }

            for (const question of section.questions) {
                bodyContent.push(...renderCRFQuestion(question, resolveStudyDateFormat(form.date_format)));
            }
        }
    } else if (form.layout === 'log-table' && form.columns) {
        // Render log-table
        const rows: TableRow[] = [];

        // Header row
        rows.push(new TableRow({
            tableHeader: true,
            children: form.columns.map(col =>
                new TableCell({
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: col.header, font: FONT_NAME, size: FONT_SIZE_SMALL, bold: true })],
                            alignment: AlignmentType.CENTER
                        })
                    ],
                    verticalAlign: VerticalAlign.CENTER
                })
            )
        }));

        // Data rows
        const numRows = form.repeating_rows || 6;
        for (let i = 0; i < numRows; i++) {
            rows.push(new TableRow({
                children: form.columns.map(col =>
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [new TextRun({
                                    text: col.type === 'date' ? dateCompactMask(resolveStudyDateFormat(form.date_format)) : col.type === 'yes_no' ? 'Y   N' : '',
                                    font: FONT_NAME,
                                    size: FONT_SIZE_SMALL
                                })],
                                alignment: AlignmentType.CENTER
                            })
                        ],
                        verticalAlign: VerticalAlign.CENTER
                    })
                )
            }));
        }

        bodyContent.push(new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            layout: TableLayoutType.FIXED,
            rows
        }));

        // Code tables
        if (form.code_tables && form.code_tables.length > 0) {
            bodyContent.push(
                new Paragraph({
                    children: [new TextRun({ text: 'Code Reference Tables', font: FONT_NAME, size: FONT_SIZE, bold: true })],
                    spacing: { before: 400, after: 200 }
                })
            );

            const codeCells = form.code_tables.map(ct => {
                const codeLines = ct.codes.map(c =>
                    new Paragraph({
                        children: [new TextRun({ text: `${c.value} = ${c.label}`, font: FONT_NAME, size: FONT_SIZE_SMALL })]
                    })
                );

                return new TableCell({
                    width: { size: Math.floor(100 / form.code_tables!.length), type: WidthType.PERCENTAGE },
                    children: [
                        new Paragraph({
                            children: [new TextRun({ text: ct.title, font: FONT_NAME, size: FONT_SIZE_SMALL, bold: true })],
                            spacing: { after: 50 }
                        }),
                        ...codeLines
                    ]
                });
            });

            bodyContent.push(new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 }
                },
                rows: [new TableRow({ children: codeCells })]
            }));
        }
    }

    // Closing block
    bodyContent.push(new Paragraph({ text: '', spacing: { before: 400 } }));

    for (const block of form.closing) {
        if (block.type === 'signature') {
            bodyContent.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: `${block.label}: `, font: FONT_NAME, size: FONT_SIZE }),
                        new TextRun({ text: '________________________________________', font: FONT_NAME, size: FONT_SIZE })
                    ],
                    spacing: { before: 100, after: 100 }
                })
            );
        } else if (block.type === 'staff_id') {
            bodyContent.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: `${block.label}: `, font: FONT_NAME, size: FONT_SIZE }),
                        new TextRun({ text: block.format || '___ ___ ___ ___', font: FONT_NAME, size: FONT_SIZE })
                    ],
                    spacing: { before: 100, after: 100 }
                })
            );
        } else if (block.type === 'investigator_signature') {
            bodyContent.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: `${block.label}: `, font: FONT_NAME, size: FONT_SIZE }),
                        new TextRun({ text: '________________________________________', font: FONT_NAME, size: FONT_SIZE }),
                        ...(block.includes_date ? [
                            new TextRun({ text: '     Date: ', font: FONT_NAME, size: FONT_SIZE }),
                            new TextRun({ text: dateMask(resolveStudyDateFormat(form.date_format)), font: FONT_NAME, size: FONT_SIZE })
                        ] : [])
                    ],
                    spacing: { before: 100, after: 100 }
                })
            );
        }
    }

    // Create document
    const doc = new Document({
        sections: [{
            properties: {
                page: {
                    size: {
                        orientation: isLandscape ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT,
                        width: isLandscape ? convertInchesToTwip(11) : convertInchesToTwip(8.5),
                        height: isLandscape ? convertInchesToTwip(8.5) : convertInchesToTwip(11)
                    },
                    margin: {
                        top: convertInchesToTwip(0.36),
                        bottom: convertInchesToTwip(0.36),
                        left: convertInchesToTwip(0.36),
                        right: convertInchesToTwip(0.225)
                    }
                }
            },
            headers: {
                default: new Header({ children: [headerTable] })
            },
            footers: {
                default: new Footer({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({ text: versionString, font: FONT_NAME, size: FONT_SIZE_SMALL }),
                                new TextRun({ text: '\t\tPage ', font: FONT_NAME, size: FONT_SIZE_SMALL }),
                                new TextRun({ children: [PageNumber.CURRENT] }),
                                new TextRun({ text: ' of ', font: FONT_NAME, size: FONT_SIZE_SMALL }),
                                new TextRun({ children: [PageNumber.TOTAL_PAGES] }),
                                new TextRun({ text: `\t\t${form.id}`, font: FONT_NAME, size: FONT_SIZE_SMALL })
                            ],
                            tabStops: [
                                { type: TabStopType.CENTER, position: convertInchesToTwip(4) },
                                { type: TabStopType.RIGHT, position: convertInchesToTwip(7.5) }
                            ]
                        })
                    ]
                })
            },
            children: bodyContent
        }]
    });

    const blob = await Packer.toBlob(doc);
    const filename = `CRF_${form.id}_${studyName}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, filename);
};

/**
 * Generate the CRF Completion Guidelines document.
 *
 * Takes the AI-generated markdown guide AND the array of CRF forms, splits the
 * markdown around per-form sections, and inserts the actual rendered CRF form
 * BEFORE each form's instructions section. This produces a document where each
 * form chapter has the CRF visible, immediately followed by its field-by-field
 * completion guidance.
 */
export const generateCRFCompletionGuidelinesDocx = async (
    markdownContent: string,
    crfForms: CRFForm[],
    metadata: CRFGuideMetadata = {},
) => {
    // The CRF artifacts in Firestore use a randomUUID as form.id, which makes
    // chapter headings unreadable. Override each form's id with a derived
    // human-readable code (e.g., "AE", "DEMO", "TF") so it matches the AI
    // chapter heading produced by the backend (which uses the same helper).
    // One rule in one place (functions/src/utils/formCodeAssignment.ts): a
    // clash gets a letter alternative, as the page assigns at creation.
    const codeMap = uniqueCodesByName(
        crfForms.filter((f): f is CRFForm => !!f?.name).map((f) => ({ name: f.name })),
    );
    const normalizedForms: CRFForm[] = crfForms.map((f) => {
        if (!f) return f;
        const derivedCode = f.name ? codeMap[f.name] : undefined;
        return {
            ...f,
            id: derivedCode || f.id || 'FORM',
        };
    });

    // Build a lookup of form IDs and names → form object.
    // Some CRF artifacts may be partial (missing id or name) — guard against
    // undefined fields rather than crashing the whole guide.
    const formByKey = new Map<string, CRFForm>();
    for (const f of normalizedForms) {
        if (f?.id) formByKey.set(String(f.id).toLowerCase(), f);
        if (f?.name) formByKey.set(String(f.name).toLowerCase(), f);
    }

    // Split the markdown by form section. The CRF generator's prompt asks the
    // AI to start each form section with a "Form header" — typically a `##` or
    // `###` heading that contains the form name and ideally the form ID in
    // brackets like "[BASE]". We split on every line that looks like such a
    // heading.
    const lines = markdownContent.split('\n');
    type Segment = { kind: 'prose'; text: string } | { kind: 'form'; form: CRFForm; text: string };
    const segments: Segment[] = [];
    let currentText: string[] = [];
    let currentForm: CRFForm | null = null;
    let inForms = false;

    const flush = () => {
        if (currentText.length === 0) return;
        const text = currentText.join('\n');
        if (currentForm) {
            segments.push({ kind: 'form', form: currentForm, text });
        } else {
            segments.push({ kind: 'prose', text });
        }
        currentText = [];
    };

    const findFormFromHeading = (heading: string): CRFForm | null => {
        if (!heading) return null;
        // Look for "[FORMID]" pattern first
        const idMatch = heading.match(/\[([^\]]+)\]/);
        if (idMatch) {
            const f = formByKey.get(idMatch[1].toLowerCase());
            if (f) return f;
        }
        // Then look for any form name appearing in the heading
        const lower = heading.toLowerCase();
        for (const f of normalizedForms) {
            if (f?.name && lower.includes(String(f.name).toLowerCase())) return f;
        }
        return null;
    };

    for (const line of lines) {
        // The generator's assembly inserts a "# Form-by-Form Completion Instructions" heading
        // before the form sections. Use this as a signal that we're now in the forms area.
        if (/^#+\s+Form-by-Form/i.test(line.trim())) {
            inForms = true;
        }

        if (inForms) {
            // Primary: markdown heading like "## [CODE] Form Name"
            const headingMatch = line.match(/^(#{2,3})\s+(.+)$/);
            if (headingMatch) {
                const possibleForm = findFormFromHeading(headingMatch[2]);
                if (possibleForm) {
                    flush();
                    currentForm = possibleForm;
                    currentText.push(line);
                    continue;
                }
            }
            // NOTE: A fallback pattern previously matched any line containing
            // [CODE] brackets for a known form, even inline cross-references
            // like "see the [CM] Concomitant Medications form". This caused
            // one form section to split into multiple segments whenever it
            // mentioned another form by code, creating duplicate sections in
            // the DOCX. Removed — the primary ## heading pattern above is
            // sufficient for the current generator output.
        }
        currentText.push(line);
    }
    flush();

    // Build the doc body
    const bodyContent: (Paragraph | Table | TableOfContents)[] = [];

    // 1. Title page
    bodyContent.push(...buildCRFGuideTitlePage(metadata));

    // 2. Approval / signature block
    bodyContent.push(...buildCRFGuideSignaturePage(metadata));

    // 3. Table of contents (Word fills page numbers on file open)
    bodyContent.push(...buildCRFGuideTOC());

    for (const seg of segments) {
        if (seg.kind === 'prose') {
            bodyContent.push(...parseMarkdownToBlocks(seg.text));
            continue;
        }

        // For each form: render the CRF first, then the instructions block
        bodyContent.push(
            new Paragraph({
                children: [new PageBreak()],
            })
        );

        // Always emit a canonical "[FORMID] Form Name" Heading 1, regardless of
        // what heading style the AI used in the markdown. This guarantees the TOC
        // picks up consistent entries.
        const lines = seg.text.split('\n');
        const headingLine = lines.find(l => /^#{2,3}\s+/.test(l));
        bodyContent.push(
            new Paragraph({
                text: `[${seg.form.id || 'FORM'}] ${seg.form.name || 'Unnamed Form'}`,
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 200, after: 200 },
            })
        );

        // Subsection: the rendered CRF
        bodyContent.push(
            new Paragraph({
                text: 'Case Report Form',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
            })
        );
        bodyContent.push(...buildCrfFormBlocks({ ...seg.form, date_format: metadata.dateFormat }, metadata.studyShortName));

        // Subsection: the instructions
        bodyContent.push(
            new Paragraph({
                text: 'Completion Instructions',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 100 },
            })
        );
        // Strip the original heading line from the prose so we don't repeat it
        const instructionLines = lines.filter(l => l !== headingLine);
        bodyContent.push(...parseMarkdownToBlocks(instructionLines.join('\n')));
    }

    const doc = new Document({
        features: { updateFields: true },     // NEW: prompts Word to update TOC on open
        styles: {
            paragraphStyles: [
                {
                    id: 'Normal',
                    name: 'Normal',
                    run: { font: 'Calibri', size: 22, color: '000000' },
                    paragraph: { spacing: { line: 276, before: 0, after: 120 } },
                },
            ],
        },
        sections: [{
            properties: {
                page: {
                    margin: {
                        top: 1080,
                        right: 1080,
                        bottom: 1080,
                        left: 1080,
                    },
                },
            },
            footers: {
                default: new Footer({
                    children: [
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new TextRun({
                                    children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES],
                                    font: 'Calibri',
                                    size: 20,
                                }),
                            ],
                        }),
                    ],
                }),
            },
            children: bodyContent,
        }],
    });

    const blob = await Packer.toBlob(doc);
    const shortCode = (metadata.studyShortName || '').trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const filename = `${shortCode ? shortCode + '_' : ''}CRF_Completion_Guidelines_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, filename);
};

/**
 * Smart document generator - detects if content is CRF and uses appropriate renderer
 */
export const generateSmartDocx = async (content: string, title: string, studyShortName?: string, dateFormat?: string) => {
    if (isStructuredCRF(content)) {
        const form = parseCRFForm(content);
        if (form) {
            await generateCrfDocx({ ...form, date_format: dateFormat }, studyShortName);
            return;
        }
    }
    // Fall back to generic document generation
    await generateDocx(content, title);
};
